import React from 'react';
import { usePomodoro, TIMER_MODES } from '../context/PomodoroContext';
import { getColors } from '../theme';
import { useApp } from '../context/AppContext';

/**
 * Focus timer in the header: a mono "Focus 24:13" button that starts and pauses the
 * timer from any page. It inks in while running, and a hairline under it shows progress.
 */
const PomodoroNavWidget = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  const {
    mode,
    isRunning,
    isActive,
    formattedTime,
    progress,
    start,
    pause,
    skip,
    settingsLoaded,
    requestNotificationPermission,
  } = usePomodoro();

  // Show immediately if running so the header doesn't flicker while settings load
  if (!settingsLoaded && !isRunning) return null;

  const label = mode === TIMER_MODES.WORK ? 'Focus' : 'Break';
  const state = isRunning ? 'running' : isActive ? 'paused' : 'ready';

  const handleClick = () => {
    if (isRunning) {
      pause();
    } else {
      requestNotificationPermission();
      start();
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={handleClick}
        aria-pressed={isRunning}
        title={isRunning ? `Pause ${label.toLowerCase()} timer` : `Start ${label.toLowerCase()} timer`}
        className="relative font-mono text-xs tabular-nums whitespace-nowrap px-2 py-1 transition-colors duration-150"
        style={{
          color: isRunning ? colors.onAccent : colors.textPrimary,
          backgroundColor: isRunning ? colors.textPrimary : 'transparent',
          border: `1px solid ${isRunning ? colors.textPrimary : colors.borderStrong}`,
        }}
      >
        <span className="hidden sm:inline">{state === 'paused' ? 'Paused' : label} </span>
        {formattedTime}
        {isActive && (
          <span
            aria-hidden="true"
            className="absolute left-0 -bottom-[4px] h-px transition-[width] duration-300 ease-linear"
            style={{ width: `${progress * 100}%`, backgroundColor: colors.textPrimary }}
          />
        )}
      </button>
      {isActive && (
        <button
          type="button"
          onClick={skip}
          title={`Skip to ${mode === TIMER_MODES.WORK ? 'break' : 'focus'}`}
          aria-label="Skip to the next session"
          className="font-mono text-xs px-1 py-1 transition-opacity hover:opacity-60"
          style={{ color: colors.textSecondary }}
        >
          »
        </button>
      )}
    </div>
  );
};

export default PomodoroNavWidget;
